
import { useState, useEffect, useCallback } from 'react';
import { NotificationSoundPreferences, NotificationSoundType } from '@/types/notificationSounds';
import { enhancedToast } from '@/utils/notifications/enhancedToast';

const DEFAULT_PREFERENCES: NotificationSoundPreferences = {
  enabled: true,
  volume: 0.7,
  sounds: {
    success: 'success.mp3',
    error: 'error.mp3',
    warning: 'warning.mp3',
    info: 'notification.mp3',
    document: 'document.mp3',
    payment: 'payment.mp3',
    attendance: 'attendance.mp3'
  }
};

export const useNotificationSounds = () => {
  const [preferences, setPreferences] = useState<NotificationSoundPreferences>(DEFAULT_PREFERENCES);
  
  // Load preferences on mount
  useEffect(() => {
    try {
      const savedPrefs = localStorage.getItem('notification_sound_preferences');
      if (savedPrefs) {
        const parsedPrefs = JSON.parse(savedPrefs);
        
        // Ensure all required sound types are present
        const sounds = { ...DEFAULT_PREFERENCES.sounds, ...(parsedPrefs.sounds || {}) };
        
        setPreferences({
          ...DEFAULT_PREFERENCES,
          ...parsedPrefs,
          sounds
        });
      }
    } catch (error) {
      console.error('Error loading notification sound preferences:', error);
    }
  }, []);
  
  // Save preferences when they change
  const updatePreferences = useCallback((newPrefs: Partial<NotificationSoundPreferences>) => {
    const updatedPrefs = {
      ...preferences,
      ...newPrefs
    };
    setPreferences(updatedPrefs);
    
    try {
      localStorage.setItem('notification_sound_preferences', JSON.stringify(updatedPrefs));
    } catch (error) {
      console.error('Error saving notification sound preferences:', error);
    }
  }, [preferences]);
  
  // Check if notifications should be muted based on quiet hours
  const shouldMute = useCallback((): boolean => {
    if (!preferences.muteTimeStart || !preferences.muteTimeEnd) return false;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    const [startHour, startMinute] = preferences.muteTimeStart.split(':').map(Number);
    const [endHour, endMinute] = preferences.muteTimeEnd.split(':').map(Number);
    
    const currentTime = currentHour * 60 + currentMinute;
    const startTime = startHour * 60 + startMinute;
    const endTime = endHour * 60 + endMinute;
    
    if (startTime <= endTime) {
      // Simple case: start time is before end time
      return currentTime >= startTime && currentTime <= endTime;
    } else {
      // Complex case: quiet hours span midnight
      return currentTime >= startTime || currentTime <= endTime;
    }
  }, [preferences]);

  // Play notification sound based on type
  const playNotificationSound = useCallback((type: NotificationSoundType, message?: string) => {
    if (!preferences.enabled || shouldMute()) return;

    switch (type) {
      case 'success':
        if (message) enhancedToast.success('Success', message);
        break;
      case 'error':
        if (message) enhancedToast.error('Error', message);
        break;
      case 'warning':
        if (message) enhancedToast.warning('Warning', message);
        break;
      case 'info':
        if (message) enhancedToast.info('Info', message);
        break;
      case 'payment':
        if (message) enhancedToast.error('Payment Due', message);
        break;
      case 'attendance':
        if (message) enhancedToast.warning('Attendance Alert', message);
        break;
      case 'document':
        if (message) enhancedToast.info('Document Update', message);
        break;
      default:
        if (message) enhancedToast.info('Notification', message);
        break;
    }
  }, [preferences, shouldMute]);
  
  return {
    preferences,
    updatePreferences,
    shouldMute,
    playNotificationSound
  };
};
